/**
 * compositorEngine.ts — Pure TypeScript image composition engine.
 *
 * No React dependencies. Handles:
 * - Loading images from URLs
 * - Mask luminance → alpha conversion
 * - Per-layer coloring (fill base color, clip by mask)
 * - Per-layer highlight blending (screen + multiply)
 * - Global highlight application
 * - Global mask clipping
 *
 * All operations use OffscreenCanvas when available, falling back to
 * document.createElement("canvas").
 */

import * as webglFiltersDebugLib from "webgl-filters";
import { type Filter } from "webgl-filters";
import { resolveSpecsImageUrl } from "./specsAssetUrls";

const { customShader, glFilters } = webglFiltersDebugLib;

// ── Types ──────────────────────────────────────────────────────────────

export interface CompositeLayer {
  maskUrl: string | null;
  highlightUrl: string | null;
  baseColor: string;
  baseTextureUrl?: string | null;
  skipFill?: boolean;
}

export interface GlobalHighlight {
  highlightUrl: string | null;
  maskUrl: string | null;
}

export interface CompositeStep {
  label: string;
  dataUrl: string;
  debugUrl?: string | null;
}

export interface CompositeResult {
  dataUrl: string;
  width: number;
  height: number;
  steps: CompositeStep[];
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

const MASK_TO_ALPHA_SHADER = customShader({
  source: `
    vec4 color = texture2D(u_texture, v_texCoord);
    float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));
    gl_FragColor = vec4(1.0, 1.0, 1.0, lum);
  `,
});

const TINT_ALPHA_SHADER_SOURCE = `
  vec4 color = texture2D(u_texture, v_texCoord);
  float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));
  gl_FragColor = vec4(u_tint[0], u_tint[1], u_tint[2], lum * u_opacity);
`;

const COLOR_THROUGH_ALPHA_SHADER_SOURCE = `
  vec4 alphaMask = texture2D(u_texture, v_texCoord);
  gl_FragColor = vec4(u_color[0], u_color[1], u_color[2], alphaMask.a);
`;

const TEXTURE_THROUGH_ALPHA_SHADER_SOURCE = `
  vec4 color = texture2D(u_texture, v_texCoord);
  vec4 alphaMask = texture2D(u_alphaMask, v_texCoord);
  gl_FragColor = vec4(color.rgb, color.a * alphaMask.a);
`;

const SOLID_HIGHLIGHT_SHADER_SOURCE = `
  vec4 highlight = texture2D(u_texture, v_texCoord);
  vec3 base = vec3(u_baseColor[0], u_baseColor[1], u_baseColor[2]);
  vec3 screen = 1.0 - (1.0 - highlight.rgb * 0.12) * (1.0 - base);
  vec3 highlighted = screen * highlight.rgb;
  gl_FragColor = vec4(highlighted, highlight.a);
`;

const SOLID_HIGHLIGHT_WITH_MASK_SHADER_SOURCE = `
  vec4 highlight = texture2D(u_texture, v_texCoord);
  vec4 alphaMask = texture2D(u_alphaMask, v_texCoord);
  vec3 base = vec3(u_baseColor[0], u_baseColor[1], u_baseColor[2]);
  vec3 screen = 1.0 - (1.0 - highlight.rgb * 0.12) * (1.0 - base);
  vec3 highlighted = screen * highlight.rgb;
  gl_FragColor = vec4(highlighted, highlight.a * alphaMask.a);
`;

const IMAGE_HIGHLIGHT_SHADER_SOURCE = `
  vec4 base = texture2D(u_texture, v_texCoord);
  vec4 highlight = texture2D(u_highlight, v_texCoord);
  vec3 screen = 1.0 - (1.0 - highlight.rgb * 0.12) * (1.0 - base.rgb);
  vec3 highlighted = screen * highlight.rgb;
  vec3 rgb = highlighted * highlight.a + base.rgb * (1.0 - highlight.a);
  gl_FragColor = vec4(rgb, base.a);
`;

type ShaderDrawResult = {
  debugUrl: string | null;
};

// ── Logging ────────────────────────────────────────────────────────────

function logError(msg: string, ...args: unknown[]) {
  console.error(`[CompositorEngine] ${msg}`, ...args);
}

// ── Image Loading ──────────────────────────────────────────────────────

/**
 * Load an image from a URL. Returns null if loading fails.
 */
export function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const resolvedUrl = resolveSpecsImageUrl(url);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => {
      logError(`Failed to load image: ${resolvedUrl}`);
      resolve(null);
    };
    img.src = resolvedUrl;
  });
}

/**
 * Load multiple images by key. Returns a map of key → HTMLImageElement.
 * Failed loads are omitted from the result (with error logs).
 */
export async function loadImages(
  entries: { key: string; url: string }[]
): Promise<Record<string, HTMLImageElement>> {
  const results: Record<string, HTMLImageElement> = {};
  const promises = entries.map(async ({ key, url }) => {
    const img = await loadImage(url);
    if (img) {
      results[key] = img;
    }
  });
  await Promise.all(promises);
  return results;
}

// ── Canvas Helpers ─────────────────────────────────────────────────────

function createCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function drawImageToCanvas(
  image: HTMLImageElement | HTMLCanvasElement,
  w: number,
  h: number
) {
  const canvas = createCanvas(w, h);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(image, 0, 0, w, h);
  return canvas;
}

function canvasToPngDataUrl(canvas: HTMLCanvasElement) {
  try {
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function exposeWebglFiltersDebugLib() {
  const debugWindow = window as typeof window & {
    webglFiltersDebugLib?: typeof webglFiltersDebugLib;
  };
  debugWindow.webglFiltersDebugLib = webglFiltersDebugLib;
}

function dataHtmlUrlToBlobUrl(dataUrl: string): string {
  const prefix = "data:text/html;base64,";
  if (!dataUrl.startsWith(prefix)) {
    return dataUrl;
  }

  const binary = atob(dataUrl.slice(prefix.length));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: "text/html" }));
}

function createWebglFiltersDebugUrl(
  filter: Filter,
  input: HTMLCanvasElement
): string | null {
  const originalLog = console.log;
  let debugUrl: string | null = null;

  // webgl-filters exposes debug pages by logging a data URL. Capture that URL
  // so the specs editor can link directly to the generated debug page.
  console.log = (...args: unknown[]) => {
    const firstArg = args[0];
    if (
      typeof firstArg === "string" &&
      firstArg.startsWith("data:text/html;base64,")
    ) {
      debugUrl = firstArg;
      return;
    }
    originalLog(...args);
  };

  try {
    glFilters().addFilter(filter).debug(input);
    if (!debugUrl) {
      return null;
    }
    exposeWebglFiltersDebugLib();
    return dataHtmlUrlToBlobUrl(debugUrl);
  } catch (err) {
    logError("Failed to build webgl-filters debug URL:", err);
    return null;
  } finally {
    console.log = originalLog;
  }
}

async function applyShaderToCanvas(
  input: HTMLCanvasElement,
  output: HTMLCanvasElement,
  filter: Filter,
  debugFilter: Filter = filter
): Promise<ShaderDrawResult> {
  await glFilters().addFilter(filter).applyToCanvasAsync(input, output);
  return {
    debugUrl: createWebglFiltersDebugUrl(debugFilter, input),
  };
}

function parseHexColor(hex: string): RGB {
  const h = hex.replace("#", "");
  if (h.length !== 6) {
    return { r: 0.5, g: 0.5, b: 0.5 };
  }
  return {
    r: parseInt(h.substring(0, 2), 16) / 255,
    g: parseInt(h.substring(2, 4), 16) / 255,
    b: parseInt(h.substring(4, 6), 16) / 255,
  };
}

// ── Core Operations ────────────────────────────────────────────────────

/**
 * Convert a grayscale mask image to an alpha-channel canvas.
 * White → opaque, black → transparent.
 */
export function maskToAlpha(
  img: HTMLImageElement,
  w: number,
  h: number
): Promise<HTMLCanvasElement> {
  const source = drawImageToCanvas(img, w, h);
  const output = createCanvas(w, h);
  return glFilters()
    .addFilter(MASK_TO_ALPHA_SHADER)
    .applyToCanvasAsync(source, output);
}

/**
 * Fill a canvas region with a solid color, clipped by a mask alpha canvas.
 * Draws onto the target context.
 */
export async function drawColoredMask(
  targetCtx: CanvasRenderingContext2D,
  maskAlpha: HTMLCanvasElement,
  color: string,
  w: number,
  h: number
): Promise<ShaderDrawResult> {
  const colorCanvas = createCanvas(w, h);
  const rgb = parseHexColor(color);
  const filter = customShader({
    source: COLOR_THROUGH_ALPHA_SHADER_SOURCE,
    uniforms: { u_color: [rgb.r, rgb.g, rgb.b] },
  });
  const result = await applyShaderToCanvas(maskAlpha, colorCanvas, filter);
  targetCtx.globalCompositeOperation = "source-over";
  targetCtx.drawImage(colorCanvas, 0, 0);
  return result;
}

/**
 * Draw a fabric texture at full canvas scale, then clip it with the layer mask.
 * The generator resizes the texture to the complete product canvas before
 * cropping mask regions, which avoids visible seams between same-texture parts.
 */
export async function drawTexturedMask(
  targetCtx: CanvasRenderingContext2D,
  maskAlpha: HTMLCanvasElement,
  texture: HTMLImageElement,
  w: number,
  h: number
): Promise<ShaderDrawResult> {
  const textureSource = drawImageToCanvas(texture, w, h);
  const textureCanvas = createCanvas(w, h);
  const filter = customShader({
    source: TEXTURE_THROUGH_ALPHA_SHADER_SOURCE,
    textures: { u_alphaMask: maskAlpha },
  });
  const maskDebugUrl = canvasToPngDataUrl(maskAlpha);
  const result = await applyShaderToCanvas(
    textureSource,
    textureCanvas,
    filter,
    customShader({
      source: TEXTURE_THROUGH_ALPHA_SHADER_SOURCE,
      textures: maskDebugUrl ? { u_alphaMask: maskDebugUrl } : undefined,
    })
  );
  targetCtx.globalCompositeOperation = "source-over";
  targetCtx.drawImage(textureCanvas, 0, 0);
  return result;
}

/**
 * Apply highlight (light & shadow) using screen+multiply blend.
 * Matches the generator's applyLightAndShadow algorithm.
 * Clips result by maskAlpha if provided.
 * Draws onto the target context.
 */
export async function drawHighlight(
  targetCtx: CanvasRenderingContext2D,
  highlight: HTMLImageElement,
  baseColor: RGB,
  maskAlpha: HTMLCanvasElement | null,
  w: number,
  h: number
): Promise<ShaderDrawResult> {
  const highlightSource = drawImageToCanvas(highlight, w, h);
  const work = createCanvas(w, h);
  const source = maskAlpha
    ? SOLID_HIGHLIGHT_WITH_MASK_SHADER_SOURCE
    : SOLID_HIGHLIGHT_SHADER_SOURCE;
  const uniforms = {
    u_baseColor: [baseColor.r, baseColor.g, baseColor.b],
  };
  const filter = customShader({
    source,
    uniforms,
    textures: maskAlpha ? { u_alphaMask: maskAlpha } : undefined,
  });
  const maskDebugUrl = maskAlpha ? canvasToPngDataUrl(maskAlpha) : null;
  const result = await applyShaderToCanvas(
    highlightSource,
    work,
    filter,
    customShader({
      source,
      uniforms,
      textures: maskDebugUrl ? { u_alphaMask: maskDebugUrl } : undefined,
    })
  );
  targetCtx.globalCompositeOperation = "source-over";
  targetCtx.drawImage(work, 0, 0);
  return result;
}

/**
 * Apply the generator highlight blend to pixels already present on a layer
 * canvas. This is used for fabric textures where every pixel has its own base
 * color before the highlight is applied.
 */
export async function applyHighlightToLayerCanvas(
  layerCtx: CanvasRenderingContext2D,
  highlight: HTMLImageElement,
  w: number,
  h: number
): Promise<ShaderDrawResult> {
  const base = layerCtx.canvas;
  const highlightSource = drawImageToCanvas(highlight, w, h);
  const output = createCanvas(w, h);
  const highlightDebugUrl = canvasToPngDataUrl(highlightSource);
  const filter = customShader({
    source: IMAGE_HIGHLIGHT_SHADER_SOURCE,
    textures: { u_highlight: highlightSource },
  });
  const result = await applyShaderToCanvas(
    base,
    output,
    filter,
    customShader({
      source: IMAGE_HIGHLIGHT_SHADER_SOURCE,
      textures: highlightDebugUrl
        ? { u_highlight: highlightDebugUrl }
        : undefined,
    })
  );
  layerCtx.clearRect(0, 0, w, h);
  layerCtx.globalCompositeOperation = "source-over";
  layerCtx.drawImage(output, 0, 0);
  return result;
}

/**
 * Apply global highlight over the entire composited canvas.
 * Reads existing pixels, blends with highlight, writes back.
 */
export async function applyGlobalHighlight(
  ctx: CanvasRenderingContext2D,
  highlight: HTMLImageElement,
  w: number,
  h: number
): Promise<ShaderDrawResult> {
  const base = ctx.canvas;
  const highlightSource = drawImageToCanvas(highlight, w, h);
  const output = createCanvas(w, h);
  const highlightDebugUrl = canvasToPngDataUrl(highlightSource);
  const filter = customShader({
    source: IMAGE_HIGHLIGHT_SHADER_SOURCE,
    textures: { u_highlight: highlightSource },
  });
  const result = await applyShaderToCanvas(
    base,
    output,
    filter,
    customShader({
      source: IMAGE_HIGHLIGHT_SHADER_SOURCE,
      textures: highlightDebugUrl
        ? { u_highlight: highlightDebugUrl }
        : undefined,
    })
  );
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";
  ctx.drawImage(output, 0, 0);
  return result;
}

/**
 * Apply global mask — clips the entire canvas to the mask silhouette.
 */
export async function applyGlobalMask(
  ctx: CanvasRenderingContext2D,
  mask: HTMLImageElement,
  w: number,
  h: number
): Promise<void> {
  const alphaCanvas = await maskToAlpha(mask, w, h);
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(alphaCanvas, 0, 0);
  ctx.globalCompositeOperation = "source-over";
}

// ── Main Compositor ────────────────────────────────────────────────────

/**
 * Composite multiple layers with optional global highlight/mask.
 *
 * Returns a CompositeResult with the data URL, or null if nothing to render.
 * Logs detailed diagnostics for each step.
 */
export async function compositeProduct(
  layers: CompositeLayer[],
  globalHighlightConfig?: GlobalHighlight | null
): Promise<CompositeResult | null> {
  const validLayers = layers.filter((l) => l.maskUrl || l.highlightUrl);

  if (validLayers.length === 0) {
    return null;
  }

  // Collect URLs to load
  const toLoad: { key: string; url: string }[] = [];
  for (let i = 0; i < validLayers.length; i++) {
    const l = validLayers[i];
    if (l.maskUrl) toLoad.push({ key: `mask-${i}`, url: l.maskUrl });

    if (l.highlightUrl) toLoad.push({ key: `hl-${i}`, url: l.highlightUrl });
    if (l.baseTextureUrl) {
      toLoad.push({ key: `texture-${i}`, url: l.baseTextureUrl });
    }
  }

  if (globalHighlightConfig?.highlightUrl) {
    toLoad.push({ key: "gHl", url: globalHighlightConfig.highlightUrl });
  }
  if (globalHighlightConfig?.maskUrl) {
    toLoad.push({ key: "gMask", url: globalHighlightConfig.maskUrl });
  }

  if (toLoad.length === 0) {
    logError("No image URLs to load — cannot produce composite");
    return null;
  }

  const images = await loadImages(toLoad);
  const loadedCount = Object.keys(images).length;

  if (loadedCount === 0) {
    logError("All images failed to load — cannot produce composite");
    return null;
  }

  // Determine canvas size from first loaded image
  const firstImg = Object.values(images)[0];
  const w = firstImg.naturalWidth || firstImg.width;
  const h = firstImg.naturalHeight || firstImg.height;

  if (!w || !h) {
    logError("First image has zero dimensions — cannot produce composite");
    return null;
  }

  const canvas = createCanvas(w, h);
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);

  const steps: CompositeStep[] = [];
  function snapshot(label: string, debugUrl: string | null = null) {
    try {
      steps.push({ label, dataUrl: canvas.toDataURL("image/png"), debugUrl });
    } catch {
      // CORS — skip this step snapshot
    }
  }

  // Composite each layer
  for (let i = 0; i < validLayers.length; i++) {
    const layer = validLayers[i];
    const mask = images[`mask-${i}`];
    const highlight = images[`hl-${i}`];
    const texture = images[`texture-${i}`];
    const bc = parseHexColor(layer.baseColor);
    const layerCanvas = createCanvas(w, h);
    const layerCtx = layerCanvas.getContext("2d")!;
    const stepLabels: Array<{ label: string; debugUrl: string | null }> = [];

    let maskAlpha: HTMLCanvasElement | null = null;
    if (mask) {
      maskAlpha = await maskToAlpha(mask, w, h);
      if (layer.skipFill) {
        stepLabels.push({
          label: `Layer ${i}: fill skipped`,
          debugUrl: null,
        });
      } else if (texture) {
        const result = await drawTexturedMask(
          layerCtx,
          maskAlpha,
          texture,
          w,
          h
        );
        stepLabels.push({
          label: `Layer ${i}: texture fill (${layer.baseTextureUrl})`,
          debugUrl: result.debugUrl,
        });
      } else {
        const result = await drawColoredMask(
          layerCtx,
          maskAlpha,
          layer.baseColor,
          w,
          h
        );
        stepLabels.push({
          label: `Layer ${i}: color fill (${layer.baseColor})`,
          debugUrl: result.debugUrl,
        });
      }
    }

    if (highlight) {
      if (texture) {
        const result = await applyHighlightToLayerCanvas(
          layerCtx,
          highlight,
          w,
          h
        );
        stepLabels.push({
          label: `Layer ${i}: + highlight`,
          debugUrl: result.debugUrl,
        });
      } else {
        const result = await drawHighlight(
          layerCtx,
          highlight,
          bc,
          maskAlpha,
          w,
          h
        );
        stepLabels.push({
          label: `Layer ${i}: + highlight`,
          debugUrl: result.debugUrl,
        });
      }
    }

    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(layerCanvas, 0, 0);
    for (const step of stepLabels) {
      snapshot(step.label, step.debugUrl);
    }
  }

  // Global highlight
  const gHl = images["gHl"];
  if (gHl) {
    const result = await applyGlobalHighlight(ctx, gHl, w, h);
    snapshot("Global highlight applied", result.debugUrl);
  }

  // Global mask
  const gMask = images["gMask"];
  if (gMask) {
    await applyGlobalMask(ctx, gMask, w, h);
    snapshot("Global mask applied");
  }

  ctx.globalCompositeOperation = "source-over";

  try {
    const dataUrl = canvas.toDataURL("image/png");
    return { dataUrl, width: w, height: h, steps };
  } catch (err) {
    logError("Failed to export canvas to data URL (likely CORS issue):", err);
    return null;
  }
}

/**
 * Convert a mask image URL to a purple-tinted alpha overlay (for hover highlight).
 * Returns a data URL, or null if loading fails.
 */
export async function createMaskOverlay(
  url: string,
  tint: { r: number; g: number; b: number } = { r: 140, g: 80, b: 200 },
  opacity: number = 0.5
): Promise<string | null> {
  const img = await loadImage(url);
  if (!img) return null;

  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const canvas = createCanvas(w, h);
  await glFilters()
    .addFilter(
      customShader({
        source: TINT_ALPHA_SHADER_SOURCE,
        uniforms: {
          u_tint: [tint.r / 255, tint.g / 255, tint.b / 255],
          u_opacity: opacity,
        },
      })
    )
    .applyToCanvasAsync(img, canvas);
  return canvas.toDataURL("image/png");
}
